import React, { useEffect, useState, FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { useDebounce } from "use-debounce";
import { useMediaQuery } from "react-responsive";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectorFavorites } from "src/redux/favorites/selector";
import { getFiltersValuesAsync } from "src/redux/filters/actions";
import { selectFiltersValues } from "src/redux/filters/selectors";
import { getFavoritesAsync, ITEMS_PER_PAGE } from "src/redux/favorites/actions";
import { scrollTop } from "src/utils/scrollTop";
import {
  favoritesTrns,
  noFavoritesBannerTrns,
} from "src/page-components/cabinet/favorites/trns";
import { CloneCard } from "src/components/CloneCard";
import { Filtering } from "src/components/Filtering";
import { PageWrapper } from "src/components/Layouts/PageWrapper";
import { SortingDropdownList } from "src/components/SortingDropdownList";
import { Loader } from "src/components/Loader";
import { NoItemsBanner } from "src/components/NoItemsBanner";
import { FilteringButton } from "src/components/Filtering/FilteringButton";
import { MobFilteringMenu } from "src/components/Filtering/MobFilteringMenu";
import { Pagination } from "src/components/Pagination";
import { SORTING_OPTIONS } from "src/components/SortingDropdownList/constants";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import { PATHNAMES } from "src/constants/routes";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";
import { MetadataWrapper } from "src/components/MetadataWrapper";
import { ISelectOption } from "src/@types/form";
import { Sizes } from "src/@types/sizes";

const DEBOUNCE_DELAY_DESKTOP = 1000;
const DEBOUNCE_DELAY_MOBILE = 2500;

const Favorites: FC = () => {
  const { t } = useTranslation();
  const [isOpenFiltersMenu, setIsOpenFiltersMenu] = useState(false);

  const isGLScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.GL,
  });
  const debounceDelay = isGLScreenBreakpoint
    ? DEBOUNCE_DELAY_MOBILE
    : DEBOUNCE_DELAY_DESKTOP;

  const dispatch = useAppDispatch();

  const { queryParams, getQueryParam, setQueryParam, setMultipleQueryParams } =
    useQueryParams();

  const [debouncedQueryParams] = useDebounce(queryParams, debounceDelay);

  const filtersValues = useAppSelector(selectFiltersValues);

  useEffect(() => {
    dispatch(getFiltersValuesAsync());
  }, [dispatch]);

  const activeOption = SORTING_OPTIONS.find(
    ({ value }) => value === getQueryParam(QUERY_PARAM_KEYS.SORTING)
  );

  const setOption = ({ value }: ISelectOption) =>
    setMultipleQueryParams({
      [QUERY_PARAM_KEYS.SORTING]: value,
      [QUERY_PARAM_KEYS.PAGE]: 1,
    });

  const currentPage = Number(getQueryParam(QUERY_PARAM_KEYS.PAGE)) || 1;

  const setPage = (page: number) => {
    scrollTop();
    if (page !== currentPage) {
      setQueryParam(QUERY_PARAM_KEYS.PAGE, page.toString());
    }
  };

  const filteredQueryParams = useMemo(() => {
    const params = new URLSearchParams(debouncedQueryParams);
    params.delete(QUERY_PARAM_KEYS.IS_SHOWED_MORE);
    return params.toString();
  }, [debouncedQueryParams]);

  useEffect(() => {
    dispatch(getFavoritesAsync({ filters: filteredQueryParams }));
  }, [filteredQueryParams]);

  const favoritesResponse = useAppSelector(selectorFavorites);
  const { isLoading, data } = favoritesResponse || {};

  const favoritesResult = data?.results || [];

  const pageCount = Math.ceil(data?.count / ITEMS_PER_PAGE);
  const isPaginated = pageCount > 1;

  return (
    <MetadataWrapper name="favorites">
      <PageWrapper mainClassName="py-25">
        <section className="container flex flex-col gap-3 gl:gap-10">
          <h1 className="font-semibold">{t(favoritesTrns.title)}</h1>

          <div className="flex items-start gap-3">
            {!isGLScreenBreakpoint && (
              <Filtering
                filtersValues={filtersValues?.clone}
                pagePath={PATHNAMES.FAVORITES_CLONES}
              />
            )}

            {isLoading ? (
              <Loader size={Sizes.L} className="self-center" />
            ) : (
              <div className="flex flex-col gap-3 flex-1">
                <div className="flex justify-between gap-2">
                  {isGLScreenBreakpoint && (
                    <>
                      <FilteringButton
                        setIsOpen={setIsOpenFiltersMenu}
                        className="self-start"
                      />

                      <MobFilteringMenu
                        isOpen={isOpenFiltersMenu}
                        setIsOpen={setIsOpenFiltersMenu}
                        filtersValues={filtersValues?.clone}
                        pagePath={PATHNAMES.FAVORITES_CLONES}
                      />
                    </>
                  )}
                  <SortingDropdownList
                    options={SORTING_OPTIONS}
                    activeOption={activeOption || SORTING_OPTIONS[0]}
                    setOption={setOption}
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <div
                    className={cn("grid grid-cols-1 gap-3", {
                      "sm:grid-cols-2 md:grid-cols-1":
                        favoritesResult?.length > 1,
                    })}
                  >
                    {Boolean(favoritesResult?.length) ? (
                      favoritesResult.map((clone) => (
                        <CloneCard key={clone.id} clone={clone} />
                      ))
                    ) : (
                      <NoItemsBanner
                        title={t(noFavoritesBannerTrns.title)}
                        description={t(noFavoritesBannerTrns.description)}
                        href={PATHNAMES.CLONES}
                        textButton={t(
                          noFavoritesBannerTrns.addClonesToFavoritesButton
                        )}
                      />
                    )}
                  </div>

                  {isPaginated && (
                    <>
                      <Pagination
                        className="self-center my-8"
                        page={currentPage}
                        pageCount={pageCount}
                        setPage={setPage}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </PageWrapper>
    </MetadataWrapper>
  );
};

export default Favorites;
